import { useState } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import Button from 'components/base/Button';
import {
  retireAsset,
  reactivateAsset,
  RetireAssetPayload
} from 'api/assetsAPI';

interface RetireAssetModalProps {
  show: boolean;
  handleClose: () => void;
  asset: {
    id: number;
    name: string;
    serial_number: string;
    status: string;
    current_holder_name?: string;
  } | null;
  onRetireSuccess: () => void;
  mode: 'retire' | 'reactivate';
}

const RetireAssetModal = ({
  show,
  handleClose,
  asset,
  onRetireSuccess,
  mode
}: RetireAssetModalProps) => {
  const [retireReason, setRetireReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!asset) return;

    setLoading(true);
    setError(null);

    try {
      if (mode === 'retire') {
        const payload: RetireAssetPayload = {};

        // Only add retire_reason if it's not empty
        if (retireReason.trim()) {
          payload.retire_reason = retireReason.trim();
        }

        await retireAsset(asset.id, payload);
      } else {
        await reactivateAsset(asset.id);
      }

      // Reset form
      setRetireReason('');

      onRetireSuccess();
      handleClose();
    } catch (err) {
      console.error('Failed to update retire status:', err);

      // Extract error message
      const axiosError = err as {
        response?: {
          data?: { detail?: string; message?: string };
        };
        message?: string;
      };

      const errorMessage =
        axiosError.response?.data?.detail ||
        axiosError.response?.data?.message ||
        axiosError.message ||
        'Failed to update retire status. Please try again.';

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setRetireReason('');
    setError(null);
    handleClose();
  };

  if (!asset) return null;

  // Check if asset is assigned when trying to retire
  const isAssigned = asset.status === 'assigned';

  return (
    <Modal show={show} onHide={handleModalClose} centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-6">
          {mode === 'retire' ? 'Retire Device' : 'Reactivate Device'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {/* Warning if asset is assigned */}
          {mode === 'retire' && isAssigned && (
            <Alert variant="warning" className="mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <small>
                This device is currently assigned to {asset.current_holder_name}
                . Please return the device before retiring it.
              </small>
            </Alert>
          )}

          {/* Asset Information */}
          <div className="mb-3 p-3 bg-body-highlight rounded">
            <h6 className="mb-2 fs-7">Device Information</h6>
            <div className="row">
              <div className="col-6">
                <small className="text-body-tertiary d-block">Asset Name</small>
                <div className="fw-semibold fs-8">{asset.name}</div>
              </div>
              <div className="col-6">
                <small className="text-body-tertiary d-block">
                  Serial Number
                </small>
                <div className="fs-8">{asset.serial_number}</div>
              </div>
              <div className="col-12 mt-2">
                <small className="text-body-tertiary d-block">
                  Current Status
                </small>
                <div className="fs-8 text-capitalize">{asset.status}</div>
              </div>
              {asset.current_holder_name && (
                <div className="col-12 mt-2">
                  <small className="text-body-tertiary d-block">
                    Current Holder
                  </small>
                  <div className="fs-8">{asset.current_holder_name}</div>
                </div>
              )}
            </div>
          </div>

          {/* Retire Reason - Only for retire mode */}
          {mode === 'retire' && (
            <Form.Group className="mb-3">
              <Form.Label>Retire Reason (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={retireReason}
                onChange={e => setRetireReason(e.target.value)}
                placeholder="e.g., End of life, Damaged beyond repair, Obsolete..."
                disabled={loading || isAssigned}
              />
              <Form.Text className="text-muted">
                Provide context for why the device is being retired
              </Form.Text>
            </Form.Group>
          )}

          <Alert variant="info" className="mb-0">
            <i className="fas fa-info-circle me-2"></i>
            <small>
              {mode === 'retire' ? (
                <>
                  Retiring this device will:
                  <ul className="mb-0 mt-2">
                    <li>Set status to "Retired"</li>
                    <li>Make the device permanently unavailable</li>
                    <li>Record the retirement in history</li>
                  </ul>
                </>
              ) : (
                <>
                  Reactivating this device will:
                  <ul className="mb-0 mt-2">
                    <li>Set status to "Available"</li>
                    <li>Make the device available for assignment</li>
                    <li>Record the reactivation in history</li>
                  </ul>
                </>
              )}
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant={mode === 'retire' ? 'danger' : 'primary'}
            type="submit"
            disabled={loading || (mode === 'retire' && isAssigned)}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {mode === 'retire' ? 'Retiring...' : 'Reactivating...'}
              </>
            ) : mode === 'retire' ? (
              'Retire Device'
            ) : (
              'Reactivate Device'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default RetireAssetModal;
